import { useState } from 'react';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { PageHeader } from '@/components/layout';
import {
  Button,
  Card,
  Input,
  Select,
  Table,
  Badge,
  Modal,
  LoadingSpinner,
  ErrorSummary,
  EmptyState,
} from '@/components/common';
import { useBulkSelection, BulkActionBar, BulkSelectCheckbox, BulkSelectAllCheckbox } from '@/components/bulk';
import { applyServerErrors } from '@/utils/formErrors';
import {
  usePincodes,
  useCreatePincode,
  useUpdatePincode,
  useDeletePincode,
  useTogglePincodeActive,
  useStateLookup,
  useCityLookup,
  usePostOfficeLookup,
} from '@/hooks/useGeography';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import type { Pincode, PincodeRequest } from '@/services/geography';

const deliveryOptions = [
  { value: '', label: 'All' },
  { value: 'delivery', label: 'Delivery' },
  { value: 'non_delivery', label: 'Non-Delivery' },
];

export function PincodesPage() {
  const [page, setPage] = useState(1);
  // Global rule 1 -- search-as-you-type without a request per keystroke:
  // `search` drives the Input synchronously, `debouncedSearch` (300ms) is
  // what actually feeds the query below.
  const { term: search, debouncedTerm: debouncedSearch, setTerm: setSearch } = useDebouncedSearch();
  const [stateFilter, setStateFilter] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [deliveryFilter, setDeliveryFilter] = useState('');
  // Item 20: Post Office filter. `post_office` is free text, not a lookup
  // table, so its options come from usePostOfficeLookup (distinct values
  // actually on the rows) rather than a dropdown-service like State/City.
  const [postOfficeFilter, setPostOfficeFilter] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPincode, setEditingPincode] = useState<Pincode | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const { data: statesData } = useStateLookup();
  const { data: citiesData } = useCityLookup(stateFilter || undefined);
  const { data: postOfficesData } = usePostOfficeLookup({
    state: stateFilter || undefined,
    city: cityFilter || undefined,
  });
  const { data, isLoading, isError, error, refetch } = usePincodes(page, {
    search: debouncedSearch,
    state: stateFilter || undefined,
    city: cityFilter || undefined,
    delivery_status: deliveryFilter || undefined,
    post_office: postOfficeFilter || undefined,
  });
  const createMutation = useCreatePincode();
  const updateMutation = useUpdatePincode();
  const deleteMutation = useDeletePincode();
  const toggleActiveMutation = useTogglePincodeActive();

  // Item 20: multi-select + bulk status change/delete via the Wave 0 kit.
  const visibleIds = (data?.results || []).map((pincode) => pincode.id);
  const bulkSelection = useBulkSelection(visibleIds);

  const [formData, setFormData] = useState<PincodeRequest>({
    city: '',
    code: '',
    area_name: '',
    post_office: '',
    delivery_status: 'delivery',
    is_active: true,
  });

  const [formState, setFormState] = useState('');
  const { data: formCitiesData } = useCityLookup(formState || undefined);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [summaryMessages, setSummaryMessages] = useState<string[]>([]);

  const stateOptions = [
    { value: '', label: 'All States' },
    ...(statesData?.map(s => ({ value: s.id, label: s.name })) || []),
  ];

  const cityOptions = [
    { value: '', label: 'All Cities' },
    ...(citiesData?.map(c => ({ value: c.id, label: c.name })) || []),
  ];

  const postOfficeOptions = [
    { value: '', label: 'All Post Offices' },
    ...(postOfficesData?.map(name => ({ value: name, label: name })) || []),
  ];

  const handleOpenModal = (pincode?: Pincode) => {
    if (pincode) {
      setEditingPincode(pincode);
      setFormData({
        city: pincode.city,
        code: pincode.code,
        area_name: pincode.area_name || '',
        post_office: pincode.post_office || '',
        delivery_status: pincode.delivery_status,
        is_active: pincode.is_active,
      });
    } else {
      setEditingPincode(null);
      setFormState(stateFilter);
      setFormData({
        city: cityFilter || '',
        code: '',
        area_name: '',
        post_office: '',
        delivery_status: 'delivery',
        is_active: true,
      });
    }
    setFieldErrors({});
    setSummaryMessages([]);
    setIsModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFieldErrors({});
    setSummaryMessages([]);
    try {
      if (editingPincode) {
        await updateMutation.mutateAsync({ id: editingPincode.id, data: formData });
      } else {
        await createMutation.mutateAsync(formData);
        setPage(1);
        setSearch('');
      }
      setIsModalOpen(false);
    } catch (error) {
      const nextErrors: Record<string, string> = {};
      const messages = applyServerErrors(error, (name, err) => {
        nextErrors[name as string] = err.message;
      });
      setFieldErrors(nextErrors);
      setSummaryMessages(messages);
    }
  };

  const handleDelete = async (id: string) => {
    await deleteMutation.mutateAsync(id);
    setDeleteConfirm(null);
  };

  const columns = [
    {
      key: 'select',
      header: '',
      // See the "select all" strip above the table for the header checkbox --
      // Table's column header is plain text, so it can't live in the <th>.
      render: (pincode: Pincode) => (
        <BulkSelectCheckbox
          checked={bulkSelection.isSelected(pincode.id)}
          onChange={() => bulkSelection.toggle(pincode.id)}
          label={`Select ${pincode.code}`}
        />
      ),
    },
    {
      key: 'code',
      header: 'Pincode',
      render: (pincode: Pincode) => (
        <span className="font-mono font-medium text-gray-900 dark:text-gray-100">{pincode.code}</span>
      ),
    },
    {
      key: 'area_name',
      header: 'Area',
      render: (pincode: Pincode) => pincode.area_name || '-',
    },
    {
      key: 'post_office',
      header: 'Post Office',
      render: (pincode: Pincode) => pincode.post_office || '-',
    },
    {
      key: 'city',
      header: 'City',
      render: (pincode: Pincode) => pincode.city_name,
    },
    {
      key: 'state',
      header: 'State',
      render: (pincode: Pincode) => pincode.state_name,
    },
    {
      key: 'delivery_status',
      header: 'Delivery',
      render: (pincode: Pincode) => (
        <Badge variant={pincode.delivery_status === 'delivery' ? 'success' : 'warning'}>
          {pincode.delivery_status === 'delivery' ? 'Delivery' : 'Non-Delivery'}
        </Badge>
      ),
    },
    {
      key: 'is_active',
      header: 'Status',
      render: (pincode: Pincode) => (
        <Badge variant={pincode.is_active ? 'success' : 'error'}>
          {pincode.is_active ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (pincode: Pincode) => (
        <div className="flex items-center gap-2">
          <button
            onClick={() => toggleActiveMutation.mutate(pincode.id)}
            className={`px-2 py-1 text-xs rounded ${
              pincode.is_active
                ? 'bg-red-50 text-red-600 hover:bg-red-100'
                : 'bg-green-50 text-green-600 hover:bg-green-100'
            }`}
          >
            {pincode.is_active ? 'Deactivate' : 'Activate'}
          </button>
          <button
            onClick={() => handleOpenModal(pincode)}
            className="p-1 text-blue-600 hover:bg-blue-50 rounded"
          >
            <PencilIcon className="w-4 h-4" />
          </button>
          <button
            onClick={() => setDeleteConfirm(pincode.id)}
            className="p-1 text-red-600 hover:bg-red-50 rounded"
          >
            <TrashIcon className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (isError) {
    // A failed request must never render as "no pincodes" -- see StatesPage's
    // identical guard for the same reasoning.
    return (
      <div className="space-y-6">
        <PageHeader title="Pincodes" subtitle="Manage postal codes" />
        <Card>
          <EmptyState
            icon={<ExclamationTriangleIcon />}
            title="Could not load pincodes"
            description={error instanceof Error ? error.message : 'The request failed. Please try again.'}
            action={
              <button
                type="button"
                onClick={() => refetch()}
                className="text-sm font-medium text-primary-600 hover:text-primary-500"
              >
                Try again
              </button>
            }
          />
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Pincodes"
        subtitle="Manage postal codes"
        actions={
          <Button onClick={() => handleOpenModal()}>
            <PlusIcon className="w-4 h-4 mr-2" />
            Add Pincode
          </Button>
        }
      />

      <Card>
        <div className="p-4 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row sm:flex-wrap gap-4">
            <div className="flex-1 min-w-[12rem]">
              <Input
                placeholder="Search pincodes..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                leftIcon={<MagnifyingGlassIcon className="w-5 h-5" />}
              />
            </div>
            <div className="w-full sm:w-40">
              <Select
                options={stateOptions}
                value={stateFilter}
                onChange={(e) => {
                  setStateFilter(e.target.value);
                  setCityFilter('');
                  // The post office list is scoped by state/city (see
                  // usePostOfficeLookup above); a value picked under the old
                  // scope may not exist in the new one.
                  setPostOfficeFilter('');
                }}
              />
            </div>
            <div className="w-full sm:w-40">
              <Select
                options={cityOptions}
                value={cityFilter}
                onChange={(e) => {
                  setCityFilter(e.target.value);
                  setPostOfficeFilter('');
                }}
                disabled={!stateFilter}
              />
            </div>
            <div className="w-full sm:w-36">
              <Select
                options={deliveryOptions}
                value={deliveryFilter}
                onChange={(e) => setDeliveryFilter(e.target.value)}
              />
            </div>
            <div className="w-full sm:w-44">
              <Select
                options={postOfficeOptions}
                value={postOfficeFilter}
                onChange={(e) => setPostOfficeFilter(e.target.value)}
              />
            </div>
          </div>
        </div>

        {visibleIds.length > 0 && (
          <div className="flex items-center gap-2 px-4 py-2 border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/60">
            <BulkSelectAllCheckbox
              checked={bulkSelection.allVisibleSelected}
              indeterminate={bulkSelection.someVisibleSelected}
              onChange={bulkSelection.toggleAllVisible}
              label="Select all pincodes on this page"
            />
            <span className="text-sm text-gray-600 dark:text-gray-300">Select all on this page</span>
          </div>
        )}

        {bulkSelection.count > 0 && (
          <div className="px-4 pt-3">
            <BulkActionBar
              resource="geography/pincodes"
              selectedIds={bulkSelection.selectedIds}
              entityName="pincodes"
              onChanged={() => refetch()}
              onClear={bulkSelection.clear}
            />
          </div>
        )}

        <Table
          compact
          columns={columns}
          data={data?.results || []}
          emptyMessage="No pincodes found"
          rowKey={(pincode) => pincode.id}
        />

        {data && data.count > 20 && (
          <div className="p-4 border-t border-gray-200 flex justify-between items-center">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Showing {((page - 1) * 20) + 1} to {Math.min(page * 20, data.count)} of {data.count}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => p + 1)}
                disabled={!data.next}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingPincode ? 'Edit Pincode' : 'Add Pincode'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <ErrorSummary messages={summaryMessages} />
          <Select
            label="State"
            options={statesData?.map(s => ({ value: s.id, label: s.name })) || []}
            value={formState}
            onChange={(e) => {
              setFormState(e.target.value);
              setFormData({ ...formData, city: '' });
            }}
            error={fieldErrors.state}
            required
          />
          <Select
            label="City"
            options={formCitiesData?.map(c => ({ value: c.id, label: c.name })) || []}
            value={formData.city}
            onChange={(e) => setFormData({ ...formData, city: e.target.value })}
            disabled={!formState}
            error={fieldErrors.city}
            required
          />
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Pincode"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value })}
              placeholder="e.g., 400001"
              error={fieldErrors.code}
              required
            />
            <Input
              label="Area Name"
              value={formData.area_name}
              onChange={(e) => setFormData({ ...formData, area_name: e.target.value })}
              error={fieldErrors.area_name}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Post Office"
              value={formData.post_office}
              onChange={(e) => setFormData({ ...formData, post_office: e.target.value })}
              error={fieldErrors.post_office}
            />
            <Select
              label="Delivery Status"
              options={[
                { value: 'delivery', label: 'Delivery' },
                { value: 'non_delivery', label: 'Non-Delivery' },
              ]}
              value={formData.delivery_status}
              onChange={(e) => setFormData({ ...formData, delivery_status: e.target.value as 'delivery' | 'non_delivery' })}
              error={fieldErrors.delivery_status}
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              className="rounded border-gray-300"
            />
            <label htmlFor="is_active" className="text-sm text-gray-700 dark:text-gray-300">
              Active
            </label>
          </div>
          {fieldErrors.is_active && (
            <p className="text-sm text-red-600">{fieldErrors.is_active}</p>
          )}
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" type="button" onClick={() => setIsModalOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" isLoading={createMutation.isPending || updateMutation.isPending}>
              {editingPincode ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Delete Pincode"
      >
        <p className="text-gray-600 dark:text-gray-300">
          Are you sure you want to delete this pincode?
        </p>
        <div className="flex justify-end gap-3 mt-6">
          <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => deleteConfirm && handleDelete(deleteConfirm)}
            isLoading={deleteMutation.isPending}
          >
            Delete
          </Button>
        </div>
      </Modal>
    </div>
  );
}
